import { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, originUrl } from "@/lib/api";
import { toast } from "sonner";
import { Field, Section, PriceSummary, Stepper } from "@/pages/ConvoyageBooking";

const TABS = [
  { id: "trip", label: "Trajet ponctuel" },
  { id: "dispo", label: "Mise à disposition" },
];

export default function VTCBooking() {
  const [tab, setTab] = useState("trip");
  const [step, setStep] = useState(1);
  const [price, setPrice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [tripForm, setTripForm] = useState({
    pickup: "",
    dropoff: "",
    distance_km: "",
    pickup_datetime: "",
    passengers: 1,
    notes: "",
    full_name: "",
    email: "",
    phone: "",
  });

  const [dispoForm, setDispoForm] = useState({
    pickup: "",
    start_datetime: "",
    duration_hours: 2,
    passengers: 1,
    notes: "",
    full_name: "",
    email: "",
    phone: "",
  });

  // Auto-quote
  useEffect(() => {
    if (tab === "trip") {
      const km = parseFloat(tripForm.distance_km);
      if (!km || km <= 0 || !tripForm.pickup_datetime) {
        setPrice(null);
        return;
      }
      const ctl = new AbortController();
      api
        .post(
          "/quotes/vtc-trip",
          { distance_km: km, pickup_datetime: tripForm.pickup_datetime },
          { signal: ctl.signal },
        )
        .then((r) => setPrice(r.data))
        .catch(() => {});
      return () => ctl.abort();
    } else {
      const h = parseFloat(dispoForm.duration_hours);
      if (!h || h <= 0 || !dispoForm.start_datetime) {
        setPrice(null);
        return;
      }
      const ctl = new AbortController();
      api
        .post(
          "/quotes/vtc-dispo",
          { duration_hours: h, start_datetime: dispoForm.start_datetime },
          { signal: ctl.signal },
        )
        .then((r) => setPrice(r.data))
        .catch(() => {});
      return () => ctl.abort();
    }
  }, [tab, tripForm.distance_km, tripForm.pickup_datetime, dispoForm.duration_hours, dispoForm.start_datetime]);

  const setT = (k) => (e) => setTripForm({ ...tripForm, [k]: e?.target ? e.target.value : e });
  const setD = (k) => (e) => setDispoForm({ ...dispoForm, [k]: e?.target ? e.target.value : e });

  const canPayTrip =
    tripForm.pickup && tripForm.dropoff && parseFloat(tripForm.distance_km) > 0 &&
    tripForm.pickup_datetime && tripForm.full_name && tripForm.email && tripForm.phone;

  const canPayDispo =
    dispoForm.pickup && parseFloat(dispoForm.duration_hours) >= 2 &&
    dispoForm.start_datetime && dispoForm.full_name && dispoForm.email && dispoForm.phone;

  const submit = async () => {
    setSubmitting(true);
    try {
      if (tab === "trip") {
        const payload = {
          client: { full_name: tripForm.full_name, email: tripForm.email, phone: tripForm.phone },
          vtc_trip: {
            pickup: { label: tripForm.pickup },
            dropoff: { label: tripForm.dropoff },
            distance_km: parseFloat(tripForm.distance_km),
            pickup_datetime: tripForm.pickup_datetime,
            passengers: parseInt(tripForm.passengers, 10) || 1,
            notes: tripForm.notes || null,
          },
          origin_url: originUrl(),
        };
        const { data } = await api.post("/bookings/vtc-trip", payload);
        window.location.href = data.checkout_url;
      } else {
        const payload = {
          client: { full_name: dispoForm.full_name, email: dispoForm.email, phone: dispoForm.phone },
          vtc_dispo: {
            pickup: { label: dispoForm.pickup },
            start_datetime: dispoForm.start_datetime,
            duration_hours: parseFloat(dispoForm.duration_hours),
            passengers: parseInt(dispoForm.passengers, 10) || 1,
            notes: dispoForm.notes || null,
          },
          origin_url: originUrl(),
        };
        const { data } = await api.post("/bookings/vtc-dispo", payload);
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la création de la réservation.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="vtc-page" className="min-h-screen bg-[#050505]">
      <Header variant="vtc" />

      <div className="pt-32 pb-16 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.5em] gold uppercase">Service #02</div>
        <h1 className="mt-3 font-serif text-white text-4xl sm:text-5xl">Trajet VTC</h1>
        <p className="mt-3 text-gray-400 max-w-2xl">
          Un trajet ponctuel ou un chauffeur à votre disposition. Prise en charge
          élégante, discrète et ponctuelle.
        </p>

        {/* Tabs */}
        <div className="mt-8 inline-flex border border-white/10 p-1 bg-black">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setTab(t.id);
                setStep(1);
                setPrice(null);
              }}
              data-testid={`tab-${t.id}`}
              className={`px-6 py-2.5 text-sm tracking-wide transition-all ${
                tab === t.id
                  ? "bg-[color:var(--gold)] text-black"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-10">
          <div className="space-y-8">
            {tab === "trip" ? (
              <Section title="Détails du trajet">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Adresse de départ *">
                    <input data-testid="input-trip-pickup" value={tripForm.pickup} onChange={setT("pickup")} className="inp" />
                  </Field>
                  <Field label="Adresse d'arrivée *">
                    <input data-testid="input-trip-dropoff" value={tripForm.dropoff} onChange={setT("dropoff")} className="inp" />
                  </Field>
                  <Field label="Distance estimée (km) *">
                    <input data-testid="input-trip-distance" type="number" min={1} value={tripForm.distance_km} onChange={setT("distance_km")} className="inp" />
                  </Field>
                  <Field label="Date & heure de prise en charge *">
                    <input data-testid="input-trip-datetime" type="datetime-local" value={tripForm.pickup_datetime} onChange={setT("pickup_datetime")} className="inp" />
                  </Field>
                  <Field label="Nombre de passagers">
                    <input data-testid="input-trip-passengers" type="number" min={1} max={7} value={tripForm.passengers} onChange={setT("passengers")} className="inp" />
                  </Field>
                  <Field label="Notes">
                    <input data-testid="input-trip-notes" value={tripForm.notes} onChange={setT("notes")} placeholder="Aéroport, bagages..." className="inp" />
                  </Field>
                  <Field label="Nom complet *">
                    <input data-testid="input-trip-fullname" value={tripForm.full_name} onChange={setT("full_name")} className="inp" />
                  </Field>
                  <Field label="Email *">
                    <input data-testid="input-trip-email" type="email" value={tripForm.email} onChange={setT("email")} className="inp" />
                  </Field>
                  <Field label="Téléphone *">
                    <input data-testid="input-trip-phone" value={tripForm.phone} onChange={setT("phone")} className="inp" />
                  </Field>
                </div>
                <div className="mt-6 flex justify-end">
                  <button data-testid="btn-pay-trip" disabled={!canPayTrip || submitting} onClick={submit} className="btn-gold">
                    {submitting ? "Redirection..." : "Payer 100% du trajet"}
                  </button>
                </div>
              </Section>
            ) : (
              <Section title="Mise à disposition avec chauffeur">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Adresse de prise en charge *">
                    <input data-testid="input-dispo-pickup" value={dispoForm.pickup} onChange={setD("pickup")} className="inp" />
                  </Field>
                  <Field label="Début (date & heure) *">
                    <input data-testid="input-dispo-start" type="datetime-local" value={dispoForm.start_datetime} onChange={setD("start_datetime")} className="inp" />
                  </Field>
                  <Field label="Durée (heures, min. 2) *">
                    <input data-testid="input-dispo-hours" type="number" min={2} step="0.5" value={dispoForm.duration_hours} onChange={setD("duration_hours")} className="inp" />
                  </Field>
                  <Field label="Nombre de passagers">
                    <input data-testid="input-dispo-passengers" type="number" min={1} max={7} value={dispoForm.passengers} onChange={setD("passengers")} className="inp" />
                  </Field>
                  <Field label="Notes">
                    <input data-testid="input-dispo-notes" value={dispoForm.notes} onChange={setD("notes")} className="inp" />
                  </Field>
                  <Field label="Nom complet *">
                    <input data-testid="input-dispo-fullname" value={dispoForm.full_name} onChange={setD("full_name")} className="inp" />
                  </Field>
                  <Field label="Email *">
                    <input data-testid="input-dispo-email" type="email" value={dispoForm.email} onChange={setD("email")} className="inp" />
                  </Field>
                  <Field label="Téléphone *">
                    <input data-testid="input-dispo-phone" value={dispoForm.phone} onChange={setD("phone")} className="inp" />
                  </Field>
                </div>
                <div className="mt-6 flex justify-end">
                  <button data-testid="btn-pay-dispo" disabled={!canPayDispo || submitting} onClick={submit} className="btn-gold">
                    {submitting ? "Redirection..." : "Payer l'acompte 30%"}
                  </button>
                </div>
              </Section>
            )}
          </div>

          <PriceSummary price={price} title={tab === "trip" ? "Devis Trajet" : "Devis Mise à disposition"} />
        </div>
      </div>

      <Footer />
      <style>{`.inp{ width:100%; background:#0a0a0a; border:1px solid rgba(255,255,255,.1); color:#fff; padding:12px 14px; border-radius:2px; font-family:inherit; transition:border-color .2s;}
      .inp:focus{outline:none; border-color:var(--gold);}`}</style>
    </div>
  );
}
